import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { showAlert } from 'src/actions/globalAlert';
import { deleteSuppression } from 'src/actions/suppressions';
import { DeleteModal, Empty, PanelLoading, Subaccount, TableCollection } from 'src/components';
import ActionPopover from 'src/components/actionPopover';
import { Panel, ScreenReaderOnly } from 'src/components/matchbox';
import { TranslatableText } from 'src/components/text';
import Detail from './Detail';

export function Results(props) {
  const { results = [], loading, deleting, subaccounts, hasSubaccounts } = props;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailData, setDetailData] = useState({});
  const [deleteData, setDeleteData] = useState({ recipient: '' });

  const handleDeleteModalCancel = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const handleDetailModalCancel = useCallback(() => {
    setIsDetailModalOpen(false);
  }, []);

  function getRowData(row) {
    const { recipient, type, source, list_id, subaccount_id: subaccountId, subaccount_name } = row;
    const { hasSubaccounts, deleting } = props;
    const rowData = [
      recipient,
      type === 'transactional' ? 'Transactional' : 'Non-transactional',
      source,
      list_id || '—'
    ];

    if (hasSubaccounts) {
      rowData.push(
        <Subaccount
          id={subaccountId}
          master={!subaccountId}
          name={subaccount_name}
          shrinkLength={12}
        />
      );
    }

    rowData.push(
      <ActionPopover
        actions={[
          {
            content: 'View Details',
            onClick: () => openDetailModal(row)
          },
          {
            content: 'Delete',
            onClick: () => openDeleteModal(row),
            visible: !deleting
          }
        ]}
      />
    );

    return rowData;
  }

  function getColumns() {
    const { hasSubaccounts } = props;

    const columns = [
      { label: 'Recipient', sortKey: 'recipient' },
      { label: 'Type', sortKey: 'type', width: '15%' },
      { label: 'Source', width: '18%', sortKey: 'source' },
      { label: 'List ID', width: '15%', sortKey: 'list_id' }
    ];

    if (hasSubaccounts) {
      columns.push({
        label: 'Subaccount',
        width: '18%',
        sortKey: (row) => parseInt(row.subaccount_id, 10)
      });
    }

    columns.push({ label: <ScreenReaderOnly>Actions</ScreenReaderOnly>, width: '21%' });

    return columns;
  }

  function deleteSuppression() {
    const { showAlert, deleteSuppression } = props;
    const { recipient } = deleteData;

    return deleteSuppression(deleteData).then(() => {
      setIsDeleteModalOpen(false);

      return showAlert({
        type: 'success',
        message: `${recipient} was successfully deleted from the suppression list`
      });
    });
  }

  function openDetailModal(row) {
    setIsDetailModalOpen(true);
    setDetailData(row);
  }

  function openDeleteModal(row) {
    setIsDeleteModalOpen(true);
    setDeleteData(row);
  }

  if (loading) return <PanelLoading />;

  if (results === null) return <PlaceholderResults />;

  if (results.length === 0) return <EmptyResults />;

  const deleteModalContent = deleteData.list_id
    ? `This will remove the suppression for the "${deleteData.list_id}" mailing list only. This can not be undone!`
    : 'This can not be undone!';

  return (
    <>
      <DeleteModal
        title={`Are you sure you want to delete ${deleteData.recipient} from suppression list?`}
        open={isDeleteModalOpen}
        content={
          <p>
            <TranslatableText>{deleteModalContent}</TranslatableText>
          </p>
        }
        isPending={deleting}
        onConfirm={deleteSuppression}
        onCancel={handleDeleteModalCancel}
      />

      <Detail
        suppression={detailData}
        open={isDetailModalOpen}
        onCancel={handleDetailModalCancel}
        subaccounts={subaccounts}
        hasSubaccounts={hasSubaccounts}
      />

      <TableCollection
        columns={getColumns()}
        rows={props.results}
        getRowData={getRowData}
        defaultSortColumn="recipient"
        pagination
      />
    </>
  );
}

function PlaceholderResults() {
  return (
    <Panel.LEGACY>
      <Empty message="Choose some options to see your suppressions" />
    </Panel.LEGACY>
  );
}

function EmptyResults() {
  return (
    <Panel.LEGACY>
      <Empty message="There are no results for your current query" />
    </Panel.LEGACY>
  );
}

export default connect(null, { deleteSuppression, showAlert })(Results);
